import React, { useState, useEffect } from 'react';
import { api } from './App';
import { ChevronUp, ChevronDown } from './Icons';
import { formatDate } from './dateUtils';

const SOURCES = ['BARBRI', 'SRA Official', 'QLTS', 'Other'];

function ReadinessMeter({ readiness }) {
  if (readiness === null || readiness === undefined) return null;
  const color = readiness >= 70 ? 'var(--green)' : readiness >= 55 ? 'var(--amber)' : 'var(--red)';
  const label = readiness >= 70 ? 'On track' : readiness >= 55 ? 'Developing' : 'Needs work';
  return (
    <div className="readiness-meter">
      <div className="readiness-dial-wrap">
        <svg viewBox="0 0 120 70" width="160">
          {/* Background arc */}
          <path d="M10 65 A55 55 0 0 1 110 65" fill="none" stroke="var(--border2)" strokeWidth="10" strokeLinecap="round" />
          {/* Filled arc */}
          <path
            d={`M10 65 A55 55 0 0 1 110 65`}
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={`${(readiness / 100) * 172} 172`}
            opacity="0.85"
          />
          <text x="60" y="60" textAnchor="middle" fontSize="20" fontWeight="500" fill={color} fontFamily="var(--mono)">{readiness}%</text>
        </svg>
      </div>
      <div className="readiness-label" style={{ color }}>{label}</div>
      <div className="readiness-sub">Exam readiness tracking</div>
    </div>
  );
}

function ExamCard({ exam }) {
  const [open, setOpen] = useState(false);
  const pct = exam.overall_pct;
  const color = pct >= 70 ? 'var(--green)' : pct >= 55 ? 'var(--amber)' : 'var(--red)';

  return (
    <div className="exam-card-result">
      <div className="exam-card-header" onClick={() => setOpen(o => !o)}>
        <div className="exam-card-left">
          <span className="exam-card-num">Mock {exam.exam_number}</span>
          <span className="exam-card-source">{exam.source}</span>
          <span className="exam-card-date">{formatDate(exam.exam_date, 'full')}</span>
        </div>
        <div className="exam-card-right">
          <span className="exam-card-score" style={{ color }}>
            {pct}%
          </span>
          <span className="exam-card-detail">{exam.overall_correct}/{exam.overall_attempted}</span>
          <span className="chevron">{open ? <ChevronUp size={10} /> : <ChevronDown size={10} />}</span>
        </div>
      </div>
      {open && exam.topics && exam.topics.length > 0 && (
        <div className="exam-topic-breakdown">
          <div className="exam-topic-breakdown-title">Topic breakdown</div>
          {exam.topics.map((t, i) => {
            const tpct = t.attempted > 0 ? Math.round(t.correct / t.attempted * 100) : 0;
            const tc = tpct >= 70 ? 'var(--green)' : tpct >= 50 ? 'var(--amber)' : 'var(--red)';
            return (
              <div key={i} className="exam-topic-row">
                <span className="exam-topic-name">{t.subtopic || t.topic_name}</span>
                {t.module && <span className={`scraper-module-tag ${t.module === 'FLK1' ? 'tag-flk1' : 'tag-flk2'}`}>{t.module}</span>}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 4, background: 'var(--border2)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: tpct + '%', height: '100%', background: tc, borderRadius: 2 }} />
                  </div>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: tc, minWidth: 36 }}>{tpct}%</span>
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{t.correct}/{t.attempted}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {open && (!exam.topics || exam.topics.length === 0) && (
        <div className="exam-topic-breakdown">
          <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0' }}>No topic breakdown available for this exam</div>
        </div>
      )}
    </div>
  );
}

function ParsedExamPreview({ parsed, onSave, onDiscard, source }) {
  const [edited, setEdited] = useState(parsed);
  const [notes, setNotes] = useState('');
  const overallPct = edited.overall_attempted > 0
    ? Math.round(edited.overall_correct / edited.overall_attempted * 100) : 0;
  const overallColor = overallPct >= 70 ? 'var(--green)' : overallPct >= 55 ? 'var(--amber)' : 'var(--red)';

  return (
    <div className="scraper-preview">
      <div className="scraper-preview-title">Review parsed exam. Edit if needed before saving.</div>

      <div className="exam-overall-preview">
        <div className="exam-overall-score" style={{ color: overallColor }}>
          {overallPct}%
        </div>
        <div className="exam-overall-detail">
          <span>Overall: </span>
          <input type="number" min="0" className="scraper-num-input" value={edited.overall_correct}
            onChange={e => setEdited(prev => ({ ...prev, overall_correct: parseInt(e.target.value) || 0 }))} />
          <span> / </span>
          <input type="number" min="0" className="scraper-num-input" value={edited.overall_attempted}
            onChange={e => setEdited(prev => ({ ...prev, overall_attempted: parseInt(e.target.value) || 0 }))} />
        </div>
      </div>

      {edited.topics && edited.topics.length > 0 && (
        <div className="scraper-table" style={{ marginTop: 12 }}>
          <div className="scraper-table-head">
            <span>Topic</span><span>Attempted</span><span>Correct</span><span>%</span>
          </div>
          {edited.topics.map((t, i) => (
            <div key={i} className="scraper-table-row">
              <div className="scraper-topic-cell">
                <span className="scraper-topic-name">{t.topic_name}</span>
                {t.topic_id ? <span className="scraper-matched">matched</span> : <span className="scraper-unmatched">unmatched</span>}
              </div>
              <input type="number" min="0" className="scraper-num-input" value={t.attempted}
                onChange={e => setEdited(prev => ({ ...prev, topics: prev.topics.map((x, j) => j === i ? { ...x, attempted: parseInt(e.target.value) || 0 } : x) }))} />
              <input type="number" min="0" className="scraper-num-input" value={t.correct}
                onChange={e => setEdited(prev => ({ ...prev, topics: prev.topics.map((x, j) => j === i ? { ...x, correct: parseInt(e.target.value) || 0 } : x) }))} />
              <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: t.attempted > 0 && Math.round(t.correct/t.attempted*100) >= 70 ? 'var(--green)' : 'var(--amber)' }}>
                {t.attempted > 0 ? Math.round(t.correct / t.attempted * 100) + '%' : '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="field-group" style={{ marginTop: 12 }}>
        <label className="field-label">Notes (optional)</label>
        <input className="field-select" type="text" placeholder="e.g. Sat under timed conditions, felt rushed on property"
          value={notes} onChange={e => setNotes(e.target.value)} />
      </div>

      <div className="scraper-actions">
        <button className="scraper-save-btn" onClick={() => onSave(edited, notes)}>Save exam result</button>
        <button className="scraper-discard-btn" onClick={onDiscard}>Discard</button>
      </div>
    </div>
  );
}

export default function PracticeExams() {
  const [source, setSource] = useState('BARBRI');
  const [pastedText, setPastedText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [rawForSave, setRawForSave] = useState('');
  const [error, setError] = useState('');
  const [exams, setExams] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  const loadData = () => {
    Promise.all([api('/api/practice-exams'), api('/api/exam-readiness')]).then(([e, r]) => {
      setExams(e);
      setReadiness(r);
      setLoading(false);
    });
  };

  useEffect(() => { loadData(); }, [saved]);

  const handleParse = async () => {
    if (!pastedText.trim()) return;
    setParsing(true); setError('');
    try {
      const res = await api('/api/practice-exams/parse', {
        method: 'POST',
        body: JSON.stringify({ text: pastedText, source }),
      });
      if (res.error) setError(res.error);
      else { setParsed(res.parsed); setRawForSave(pastedText); }
    } catch (e) { setError('Failed to parse. Check server logs.'); }
    setParsing(false);
  };

  const handleSave = async (editedParsed, notes) => {
    await api('/api/practice-exams/save', {
      method: 'POST',
      body: JSON.stringify({ parsed: editedParsed, raw: rawForSave, source, notes }),
    });
    setParsed(null); setPastedText(''); setSaved(s => !s);
  };

  return (
    <div className="scraper-page">
      <div className="scraper-intro">
        <div className="scraper-intro-title">Practice exams</div>
        <p className="scraper-intro-text">
          Log your full mock exam results here. Enter your overall score and topic breakdown. Your exam readiness blends your mock performance (60%) with your average confidence (40%).
        </p>
      </div>

      {readiness && (
        <div className="readiness-panel">
          <ReadinessMeter readiness={readiness.readiness} />
          <div className="readiness-stats">
            <div className="readiness-stat">
              <span className="readiness-stat-val">{readiness.avg_confidence}%</span>
              <span className="readiness-stat-label">Avg confidence</span>
            </div>
            {readiness.latest_exam_pct !== null && (
              <div className="readiness-stat">
                <span className="readiness-stat-val" style={{
                  color: readiness.latest_exam_pct >= 70 ? 'var(--green)' : readiness.latest_exam_pct >= 55 ? 'var(--amber)' : 'var(--red)'
                }}>{readiness.latest_exam_pct}%</span>
                <span className="readiness-stat-label">Latest mock</span>
              </div>
            )}
            {readiness.exam_trend !== null && (
              <div className="readiness-stat">
                <span className="readiness-stat-val" style={{
                  color: readiness.exam_trend > 0 ? 'var(--green)' : readiness.exam_trend < 0 ? 'var(--red)' : 'var(--muted)'
                }}>
                  {readiness.exam_trend > 0 ? '+' : ''}{readiness.exam_trend}%
                </span>
                <span className="readiness-stat-label">Mock trend</span>
              </div>
            )}
            <div className="readiness-stat">
              <span className="readiness-stat-val">{readiness.exam_count}</span>
              <span className="readiness-stat-label">Mocks done</span>
            </div>
            <div className="readiness-stat">
              <span className="readiness-stat-val" style={{ color: 'var(--red)' }}>{readiness.weak_topics}</span>
              <span className="readiness-stat-label">Weak topics</span>
            </div>
          </div>
          <div className="readiness-note">
            {readiness.exam_count > 0 && readiness.latest_exam_pct !== null
              ? `Based on your latest mock (${readiness.latest_exam_pct}%) and your average confidence (${readiness.avg_confidence}%). The more mocks you log, the more accurate this becomes.`
              : 'Log a mock exam to see your readiness score. Until then, your preparation tracking on the Planner uses your confidence ratings.'
            }
          </div>
        </div>
      )}

      {!parsed && (
        <div className="scraper-input-section">
          <div className="source-picker">
            {SOURCES.map(s => (
              <button key={s} className={`source-btn ${source === s ? 'active' : ''}`} onClick={() => setSource(s)}>{s}</button>
            ))}
          </div>
          <textarea className="scraper-textarea"
            placeholder={`Paste your ${source} practice exam results here. Include the overall score and any topic breakdown if shown.`}
            value={pastedText} onChange={e => setPastedText(e.target.value)} />
          {error && <div className="scraper-error">{error}</div>}
          <button className="scraper-parse-btn" onClick={handleParse} disabled={parsing || !pastedText.trim()}>
            {parsing ? 'Loading…' : `Enter ${source} exam results →`}
          </button>
        </div>
      )}

      {parsed && (
        <ParsedExamPreview parsed={parsed} source={source} onSave={handleSave} onDiscard={() => setParsed(null)} />
      )}

      {!loading && exams.length > 0 && (
        <div className="scraper-summary-section">
          <div className="scraper-summary-title">Mock exam history</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {exams.map(e => <ExamCard key={e.id} exam={e} />)}
          </div>
        </div>
      )}

      {!loading && exams.length === 0 && (
        <div className="scraper-empty" style={{ marginTop: 16 }}>
          No mock exams logged yet. Use the form above to log your first result. Your readiness score will appear once you do.
        </div>
      )}
    </div>
  );
}
